import { Link as SwanLink } from '@vp/swan';

interface EmbeddableLinkCreatorProps {
  ariaDescribedBy?: string;
  children?: React.ReactNode;
  href?: string;
  isCta?: boolean;
  navToNewWindow?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  testId?: string;
}

const EmbeddableLink = (props: EmbeddableLinkCreatorProps) => {
  const {
    href,
    onClick,
    children,
    isCta,
    navToNewWindow,
    ariaDescribedBy,
    testId,
  } = props;

  if (!(href || onClick)) {
    return children;
  }

  /**
   * A Link component should use an <a> tag if it performs a navigation when clicked (i.e. redirect to cart),
   * and a <button> tag when it performs a non-navigation
   * See Swan doc: https://vista.design/swan/components/link?tab=Implementation#Semantics
   *
   * If the 'href' prop is not provided, and the `clickProp` is, the component performs a non-navigation action on click,
   * so we must override the default <a> tag with a <button> tag by including a "component" prop with "button" value
   * */
  const componentType =
    typeof onClick === 'function' && !href ? 'button' : undefined;

  return (
    <SwanLink
      component={componentType}
      aria-describedby={ariaDescribedBy || ''}
      data-testid={testId || ''}
      href={href}
      key={href}
      onClick={onClick}
      skin={isCta ? 'cta' : undefined}
      target={navToNewWindow ? '_blank' : '_self'}
    >
      {children}
    </SwanLink>
  );
};

/**
 * Component builder to build specifically a Linked Button
 * (using either 'href' or 'onClick' properties) that is suitable to be used
 * with the ReplaceTokens facility.  The optional 'navToNewWindow' allows specifying
 * that the response will display on a new window.
 *
 * Usage:
 *    const GoToBasketLink = EmbeddableLinkCreator({ href: navToCart });
 *    const GoToBillingAddressLink = EmbeddableLinkCreator({ onClick: () => navigate(BILLING_ADDRESS_STEP) });
 * @param {*} param0
 */
export const EmbeddableLinkCreator =
  ({
    href,
    onClick,
    isCta,
    ariaDescribedBy,
    testId,
  }: EmbeddableLinkCreatorProps) =>
  ({ children }: EmbeddableLinkCreatorProps) => {
    return EmbeddableLink({
      href,
      onClick,
      isCta,
      ariaDescribedBy,
      testId,
      children,
    });
  };
